import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CartContext } from "./useCart";
import { apiFetch } from "../api/client";
import { ga4, type TrackProduct } from "../analytics/ga4";

/* Adatta un CartItem al formato prodotto per GA4 (item_id = slug, fallback UUID). */
function toTrack(i: { slug?: string; id: string; title: string; price_cents: number }): TrackProduct {
  return { item_id: i.slug || i.id, item_name: i.title, price_cents: i.price_cents };
}

export interface CartSubItem {
  title: string;
  price_cents: number;
  image?: string;
}

export interface CartItem {
  id: string;
  title: string;
  slug: string;
  price_cents: number;
  currency: string;
  quantity: number;
  image?: string;
  subItems?: CartSubItem[];
}

/* ── Quote del carrello: calcolato dal backend (MOQ + sconti commerciali) ── */
export interface CartQuoteLine {
  product_id: string;
  title: string;
  quantity: number;
  unit_price_cents: number;
  moq: number | null;
  moq_met: boolean;
  qty_discount_pct: number;
  advance_discount_pct: number;
  line_discount_pct: number;
  line_subtotal_cents: number;
  line_discount_cents: number;
  line_total_cents: number;
}

export interface CartQuote {
  lines: CartQuoteLine[];
  advance_payment: boolean;
  subtotal_cents: number;
  total_discount_cents: number;
  total_cents: number;            // NETTO (imponibile) — retrocompat
  shipping_cents: number;
  shipping_included: boolean;
  prices_vat_excluded: boolean;
  // ── Ripartizione IVA (aliquota unica piatta). vat_rate_percent 0 = IVA non applicata ──
  taxable_cents: number;          // imponibile netto (dopo sconti)
  vat_rate_percent: number;       // es. 7
  vat_cents: number;              // importo IVA
  total_gross_cents: number;      // TOTALE IVA inclusa (da mostrare come totale)
  can_checkout: boolean;
  errors: string[];
}

export interface CartState {
  items: CartItem[];
  isOpen: boolean;
  totalItems: number;
  totalCents: number;
  advancePayment: boolean;
  quote: CartQuote | null;
  addItem: (product: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, qty: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  setOpen: (open: boolean) => void;
}


const STORAGE_KEY = "gb_cart";

/**
 * Il minimo d'ordine non si decide qui.
 *
 * Qui c'era `MIN_PACK_QTY = 36`, dichiarato come «standard di vendita: ogni
 * prodotto si acquista in confezioni da almeno 36 pezzi». Non è mai stato vero
 * per nessun pacco tranne Marzamemi: i minimi reali sono 18, 24 e 36, li
 * stabilisce `commercial_terms.py` e arrivano al frontend nel campo `moq` del
 * prodotto. Quel 36 era una terza fonte di verità che contraddiceva le altre
 * due, e si faceva vedere ogni volta che il `moq` mancava — aggiungendo 36
 * pezzi di un pacco il cui minimo era 18, o 24.
 *
 * Ora il frontend non inventa più un minimo: usa quello del prodotto e, se il
 * backend dice che non ce n'è uno, il minimo è 1. Un numero inventato qui non
 * verrebbe comunque rispettato al checkout, dove a decidere è il server.
 */

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* archiviazione bloccata o piena: il carrello vive solo in questa pagina */
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isOpen, setOpen] = useState(false);
  // O1 — pagamento anticipato −2%: sempre attivo, non più opzionale (nessuna checkbox).
  const advancePayment = true;
  const [quote, setQuote] = useState<CartQuote | null>(null);
  // Stato carrello sempre aggiornato per gli eventi GA4 (evita di leggere valori
  // stale dentro le callback e di emettere eventi dal reducer, che in StrictMode
  // gira due volte). openedByAdd distingue l'apertura automatica dopo "aggiungi"
  // (già tracciata da add_to_cart) da un'apertura volontaria del carrello.
  const itemsRef = useRef<CartItem[]>(items);
  const openedByAddRef = useRef(false);

  useEffect(() => {
    saveCart(items);
    itemsRef.current = items;
  }, [items]);

  // view_cart: solo su apertura volontaria del carrello (non dopo un add_to_cart).
  useEffect(() => {
    if (!isOpen) { openedByAddRef.current = false; return; }
    if (openedByAddRef.current) { openedByAddRef.current = false; return; }
    const its = itemsRef.current;
    if (its.length === 0) return;
    ga4.viewCart(
      its.map((i) => ({ item_id: i.slug || i.id, item_name: i.title, price: i.price_cents / 100, quantity: i.quantity })),
      its.reduce((s, i) => s + i.price_cents * i.quantity, 0),
      its[0]?.currency || "EUR",
    );
  }, [isOpen]);

  // Clear cart when user logs out
  useEffect(() => {
    const handleLogout = () => setItems([]);
    window.addEventListener("gb:logout", handleLogout);
    return () => window.removeEventListener("gb:logout", handleLogout);
  }, []);

  // Quote dal backend: applica MOQ + sconti commerciali. Il frontend NON calcola.
  // Carrello vuoto: nessun preventivo (`currentQuote`, derivato più sotto).
  useEffect(() => {
    if (items.length === 0) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      apiFetch<CartQuote>("/api/v1/shop/cart/quote", {
        method: "POST",
        body: JSON.stringify({
          items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
          advance_payment: advancePayment,
        }),
      })
        .then((q) => { if (!cancelled) setQuote(q); })
        .catch(() => { /* rete ko: il carrello resta usabile, niente breakdown */ });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [items, advancePayment]);

  const addItem = useCallback((product: Omit<CartItem, "quantity">, quantity?: number) => {
    // GA4 add_to_cart: la quantità aggiunta con questo click (delta).
    const addedQty = quantity ?? 1;
    ga4.addToCart(toTrack(product), addedQty, product.currency);
    openedByAddRef.current = true;
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        // Riga già presente: incrementa. Le card ripassano il MOQ, quindi un
        // secondo "aggiungi al carrello" aggiunge un altro pacco intero
        // (Roma 18 → 36): è coerente con un bottone che non ha selettore
        // quantità. ConfezioneDetail passa la quantità scelta dall'utente.
        const add = quantity ?? 1;
        return prev.map((i) => (i.id === product.id ? { ...i, ...product, quantity: i.quantity + add } : i));
      }
      // Nuova riga: il chiamante passa sempre la quantità giusta — le card
      // passano il MOQ del prodotto, ConfezioneDetail la quantità scelta
      // dall'utente (già >= al MOQ), i bundle 1. Senza quantità significa che
      // quel prodotto non ha un minimo, e allora è 1.
      const initialQty = quantity ?? 1;
      return [...prev, { ...product, quantity: initialQty }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((id: string) => {
    const item = itemsRef.current.find((i) => i.id === id);
    if (item) ga4.removeFromCart(toTrack(item), item.quantity, item.currency);
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, qty: number) => {
    // GA4: una diminuzione = remove_from_cart(delta), un aumento = add_to_cart(delta).
    const item = itemsRef.current.find((i) => i.id === id);
    if (item) {
      if (qty <= 0) ga4.removeFromCart(toTrack(item), item.quantity, item.currency);
      else if (qty < item.quantity) ga4.removeFromCart(toTrack(item), item.quantity - qty, item.currency);
      else if (qty > item.quantity) ga4.addToCart(toTrack(item), qty - item.quantity, item.currency);
    }
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: qty } : i)));
    }
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const toggleCart = useCallback(() => setOpen((o) => !o), []);

  const currentQuote = items.length === 0 ? null : quote;
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalCents = items.reduce((sum, i) => sum + i.price_cents * i.quantity, 0);

  const value = useMemo(
    () => ({
      items, isOpen, totalItems, totalCents, advancePayment, quote: currentQuote,
      addItem, removeItem, updateQuantity, clearCart, toggleCart, setOpen,
    }),
    [items, isOpen, totalItems, totalCents, advancePayment, currentQuote,
      addItem, removeItem, updateQuantity, clearCart, toggleCart],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
